import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Divider,
  Grid,
  IconButton,
  Tabs,
  Tab,
  Button,
  Tooltip,
  Skeleton,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Star as StarIcon,
  Folder as FolderIcon,
  MoreVert as MoreVertIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  VideoFile as VideoIcon,
  Article as ArticleIcon,
  Code as CodeIcon,
  Archive as ArchiveIcon,
  AutoAwesome as AutoAwesomeIcon,
  ArrowForward as ArrowForwardIcon,
  FilePresent as FilePresentIcon,
  AudioFile as AudioFileIcon,
} from '@mui/icons-material';
import dashboardService from '../../services/dashboardService';
import { FileItem as LibraryFileItem } from '../../types/files';

interface FileItem extends Omit<LibraryFileItem, 'lastModified' | 'path'> {
  lastModified: string | number;
  path: string;
  itemCount?: number;
  thumbnail?: string;
}

interface FolderItem {
  id: string;
  name: string;
  itemCount: number;
  lastModified: string | number;
  path: string;
}

interface QuickAccessProps {
  loading?: boolean;
}

const QuickAccess: React.FC<QuickAccessProps> = ({ loading: propLoading = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recentFiles, setRecentFiles] = useState<FileItem[]>([]);
  const [favoriteFiles, setFavoriteFiles] = useState<FileItem[]>([]);
  const [recentFolders, setRecentFolders] = useState<FolderItem[]>([]);
  
  // Fetch data based on active tab
  useEffect(() => {
    const fetchData = async () => {
      if (propLoading) return;
      
      try {
        setLoading(true);
        setError(null);
        
        switch (tabValue) {
          case 0: // Recent
            const recent = await dashboardService.getRecentFiles();
            setRecentFiles(recent.map(item => ({
              ...item,
              lastModified: typeof item.lastModified === 'string' || typeof item.lastModified === 'number' 
                ? item.lastModified 
                : new Date().getTime(),
              path: typeof item.path === 'string' ? item.path : `/${item.id}`,
              thumbnail: typeof item.thumbnail === 'string' ? item.thumbnail : undefined
            })));
            break;
          case 1: // Favorites
            const favorites = await dashboardService.getFavoriteFiles();
            setFavoriteFiles(favorites.map(item => ({
              ...item,
              lastModified: typeof item.lastModified === 'string' || typeof item.lastModified === 'number' 
                ? item.lastModified 
                : new Date().getTime(),
              path: typeof item.path === 'string' ? item.path : `/${item.id}`,
              thumbnail: typeof item.thumbnail === 'string' ? item.thumbnail : undefined
            })));
            break;
          case 2: // Folders
            const folders = await dashboardService.getRecentFolders();
            setRecentFolders(folders.map(item => ({
              ...item,
              lastModified: typeof item.lastModified === 'string' || typeof item.lastModified === 'number' 
                ? item.lastModified 
                : new Date().getTime(),
              path: typeof item.path === 'string' ? item.path : `/${item.id}`
            })));
            break;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tabValue, propLoading]);
  
  // Tab change handler
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle item click
  const handleItemClick = (path: string) => {
    navigate(path);
  };

  // Get file icon based on type
  const getFileIcon = (file: FileItem) => {
    const extension = typeof file.extension === 'string' ? file.extension.toLowerCase() : '';
    
    if (file.type === 'folder') {
      return <FolderIcon sx={{ color: theme.palette.warning.main }} />;
    }
    
    switch (extension) {
      case 'pdf':
        return <PdfIcon sx={{ color: theme.palette.error.main }} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
        return <ImageIcon sx={{ color: theme.palette.info.main }} />;
      case 'mp4':
      case 'mov':
      case 'avi':
      case 'wmv':
        return <VideoIcon sx={{ color: theme.palette.error.main }} />;
      case 'mp3':
      case 'wav':
      case 'ogg':
        return <AudioFileIcon sx={{ color: theme.palette.warning.main }} />;
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz':
        return <ArchiveIcon sx={{ color: theme.palette.success.main }} />;
      case 'js':
      case 'ts':
      case 'jsx':
      case 'tsx':
      case 'py':
      case 'java':
      case 'cpp':
      case 'c':
      case 'h':
      case 'php':
      case 'html':
      case 'css':
        return <CodeIcon sx={{ color: theme.palette.primary.main }} />;
      case 'doc':
      case 'docx':
      case 'txt':
      case 'rtf':
        return <DescriptionIcon sx={{ color: theme.palette.primary.main }} />;
      default:
        return <FilePresentIcon sx={{ color: theme.palette.text.secondary }} />;
    }
  };

  const renderFileItem = (file: FileItem) => {
    const handleClick = () => {
      // Navigate to file preview page
      navigate(`/preview/${file.id}`);
    };

    return (
      <Grid item xs={12} sm={6} md={4} key={String(file.id)}>
        <Card
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
              cursor: 'pointer',
            },
          }}
          onClick={handleClick}
        >
          <CardContent sx={{ p: 2, display: 'flex', alignItems: 'flex-start' }}>
            {file.thumbnail ? (
              <Box
                component="img"
                src={file.thumbnail}
                alt={String(file.name)}
                sx={{
                  width: 40,
                  height: 40,
                  objectFit: 'cover',
                  borderRadius: 1,
                  mr: 1.5,
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  mr: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  flexShrink: 0,
                }}
              >
                {getFileIcon(file)}
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {file.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {new Date(file.lastModified).toLocaleDateString()}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderFolderItem = (folder: FolderItem) => {
    const handleClick = () => {
      // Navigate to folder/drive page
      navigate(`/files/${folder.id}`);
    };

    return (
      <Grid item xs={12} sm={6} md={4} key={String(folder.id)}>
        <Card
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
              cursor: 'pointer',
            },
          }}
          onClick={handleClick}
        >
          <CardContent sx={{ p: 2, display: 'flex', alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                mr: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
                flexShrink: 0,
              }}
            >
              <FolderIcon sx={{ color: theme.palette.warning.main }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {folder.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {folder.itemCount} items • {new Date(folder.lastModified).toLocaleDateString()}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  if (propLoading) {
    return (
      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AutoAwesomeIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Quick Access</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Grid container spacing={2}>
            {Array.from(new Array(6)).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AutoAwesomeIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Quick Access</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AutoAwesomeIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Quick Access</Typography>
          </Box>
        }
        action={
          <IconButton>
            <MoreVertIcon />
          </IconButton>
        }
      />
      
      <Divider />
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            minHeight: 48,
            '& .MuiTab-root': {
              py: 1.5,
              minHeight: 48,
            }
          }}
        >
          <Tab
            icon={<AccessTimeIcon fontSize="small" />}
            label="Recent"
            iconPosition="start"
          />
          <Tab
            icon={<StarIcon fontSize="small" />}
            label="Favorites"
            iconPosition="start"
          />
          <Tab
            icon={<FolderIcon fontSize="small" />}
            label="Folders"
            iconPosition="start"
          />
        </Tabs>
      </Box>
      
      <CardContent>
        {loading ? (
          <Grid container spacing={2}>
            {Array.from(new Array(6)).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <>
            {tabValue === 0 && ( // Recent Files
              <Grid container spacing={2}>
                {recentFiles.map(renderFileItem)}
              </Grid>
            )}
            
            {tabValue === 1 && ( // Favorite Files
              <Grid container spacing={2}>
                {favoriteFiles.map(renderFileItem)}
              </Grid>
            )}
            
            {tabValue === 2 && ( // Recent Folders
              <Grid container spacing={2}>
                {recentFolders.map(renderFolderItem)}
              </Grid>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default QuickAccess; 